import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { Calculator, Plus, Trash2, Eye, CheckCircle, Send, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Allocation status labels
const ALLOCATION_STATUS = {
  DRAFT: { label: 'Bozza', color: 'bg-gray-100 text-gray-800' },
  CALCULATED: { label: 'Calcolato', color: 'bg-blue-100 text-blue-800' },
  APPROVED: { label: 'Approvato', color: 'bg-green-100 text-green-800' },
  DISTRIBUTED: { label: 'Distribuito', color: 'bg-purple-100 text-purple-800' }
};

export const Allocations = () => {
  const { token, selectedCondominium } = useAuth();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;
  
  // Data state
  const [allocations, setAllocations] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [allocationDetail, setAllocationDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  
  // Form
  const [createForm, setCreateForm] = useState({
    expense_id: '',
    table_id: '',
    notes: ''
  });

  // Fetch functions
  const fetchAllocations = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/allocations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAllocations(await res.json());
    } catch (error) {
      console.error('Error fetching allocations:', error);
    }
  }, [condoId, token]);

  const fetchExpenses = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setExpenses(await res.json());
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  }, [condoId, token]);

  const fetchTables = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/millesimal-tables?status=ACTIVE`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setTables(await res.json());
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  }, [condoId, token]);

  const fetchAllocationDetail = async (allocationId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/allocations/${allocationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAllocationDetail(data);
        setIsDetailOpen(true);
      }
    } catch (error) {
      toast.error('Errore nel caricamento dettagli');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAllocations(), fetchExpenses(), fetchTables()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAllocations, fetchExpenses, fetchTables]);

  // Create allocation
  const handleCreateAllocation = async (e) => {
    e.preventDefault();
    try {
      const tableId = createForm.table_id === 'auto' ? null : (createForm.table_id || null);
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/allocations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          expense_id: createForm.expense_id,
          table_id: tableId,
          notes: createForm.notes || null
        })
      });
      if (res.ok) {
        const result = await res.json();
        toast.success('Riparto calcolato');
        setIsCreateOpen(false);
        setCreateForm({ expense_id: '', table_id: '', notes: '' });
        fetchAllocations();
        // Show detail immediately
        setAllocationDetail(result);
        setIsDetailOpen(true);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore creazione riparto');
      }
    } catch (error) {
      toast.error('Errore creazione riparto');
    }
  };

  // Approve allocation
  const handleApprove = async (allocationId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/allocations/${allocationId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Riparto approvato');
        fetchAllocations();
        if (allocationDetail?.allocation?.id === allocationId) {
          fetchAllocationDetail(allocationId);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore approvazione');
      }
    } catch (error) {
      toast.error('Errore approvazione');
    }
  };

  // Distribute allocation (generate documents)
  const handleDistribute = async (allocationId) => {
    if (!window.confirm('Generare e distribuire i documenti di riparto a tutte le unità?')) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/allocations/${allocationId}/distribute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
        fetchAllocations();
        if (allocationDetail?.allocation?.id === allocationId) {
          fetchAllocationDetail(allocationId);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore distribuzione');
      }
    } catch (error) {
      toast.error('Errore distribuzione');
    }
  };

  // Delete allocation
  const handleDelete = async (allocationId) => {
    if (!window.confirm('Eliminare questo riparto?')) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/allocations/${allocationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Riparto eliminato');
        fetchAllocations();
        setIsDetailOpen(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore eliminazione');
      }
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Format helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const canEdit = userRole === 'ADMIN';
  const canView = true; // Tutti possono vedere i riparti (read-only per residenti)
  const isResident = userRole === 'RESIDENT';

  // Get unallocated expenses
  const allocatedExpenseIds = new Set(allocations.map(a => a.expense_id));
  const unallocatedExpenses = expenses.filter(e => !allocatedExpenseIds.has(e.id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Ripartizioni"
        subtitle="Ripartizione delle spese"
        actions={canEdit ? (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white text-primary hover:bg-white/90">
                <Plus className="mr-2 h-4 w-4" />
                Nuovo Riparto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Calcola Riparto</DialogTitle>
                <DialogDescription>
                  Seleziona una spesa da ripartire tra le unità
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateAllocation} className="space-y-4">
                <div className="space-y-2">
                  <Label>Spesa da Ripartire *</Label>
                  <Select
                    value={createForm.expense_id}
                    onValueChange={(v) => setCreateForm({ ...createForm, expense_id: v })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Seleziona spesa..." className="truncate" />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-3rem)]">
                      {unallocatedExpenses.length === 0 ? (
                        <SelectItem value="no-expenses" disabled>Nessuna spesa disponibile</SelectItem>
                      ) : (
                        unallocatedExpenses.map((exp) => (
                          <SelectItem key={exp.id} value={exp.id} className="max-w-full">
                            <span className="block truncate max-w-[350px]">
                              {exp.description?.substring(0, 40) || 'Spesa'}{exp.description?.length > 40 ? '...' : ''} - {formatCurrency(exp.total)}
                            </span>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tabella Millesimale</Label>
                  <Select
                    value={createForm.table_id}
                    onValueChange={(v) => setCreateForm({ ...createForm, table_id: v })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Automatica (da regola)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Automatica (da regola)</SelectItem>
                      {tables.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name} (v{t.version})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Lascia vuoto per usare la regola di riparto associata alla categoria spesa
                  </p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" disabled={!createForm.expense_id}>
                    Calcola Riparto
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        ) : null}
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calculator className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Totale Riparti</p>
                <p className="text-xl font-bold">{allocations.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <FileText className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Da Approvare</p>
                <p className="text-xl font-bold">
                  {allocations.filter(a => a.status === 'CALCULATED').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Approvati</p>
                <p className="text-xl font-bold">
                  {allocations.filter(a => a.status === 'APPROVED').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Send className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Distribuiti</p>
                <p className="text-xl font-bold">
                  {allocations.filter(a => a.status === 'DISTRIBUTED').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Allocations List */}
      <Card>
        <CardHeader>
          <CardTitle>Elenco Riparti</CardTitle>
          <CardDescription>
            Clicca su un riparto per vedere il dettaglio delle quote
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
          ) : allocations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nessun riparto. Crea il primo riparto da una spesa registrata.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Spesa</TableHead>
                  <TableHead>Tabella</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allocations.map((alloc) => {
                  const statusInfo = ALLOCATION_STATUS[alloc.status] || ALLOCATION_STATUS.DRAFT;
                  return (
                    <TableRow 
                      key={alloc.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => fetchAllocationDetail(alloc.id)}
                    >
                      <TableCell>{formatDate(alloc.created_at)}</TableCell>
                      <TableCell className="font-medium max-w-[200px] truncate">
                        {alloc.expense_description}
                      </TableCell>
                      <TableCell>{alloc.table_name}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(alloc.expense_total)}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => fetchAllocationDetail(alloc.id)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canEdit && alloc.status === 'CALCULATED' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleApprove(alloc.id)}
                              title="Approva"
                            >
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          {canEdit && (alloc.status === 'APPROVED' || alloc.status === 'CALCULATED') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDistribute(alloc.id)}
                              title="Distribuisci documenti"
                            >
                              <Send className="h-4 w-4 text-purple-600" />
                            </Button>
                          )}
                          {canEdit && alloc.status !== 'DISTRIBUTED' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(alloc.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Allocation Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio Riparto</DialogTitle>
            {allocationDetail?.allocation && (
              <DialogDescription>
                {allocationDetail.allocation.expense_description} • 
                Tabella: {allocationDetail.allocation.table_name}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {allocationDetail && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Totale Spesa</p>
                  <p className="text-xl font-bold">
                    {formatCurrency(allocationDetail.allocation.expense_total)}
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Totale Ripartito</p>
                  <p className="text-xl font-bold">
                    {formatCurrency(allocationDetail.allocation.total_allocated)}
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Arrotondamento</p>
                  <p className="text-xl font-bold">
                    {formatCurrency(allocationDetail.allocation.rounding_difference)}
                  </p>
                </div>
              </div>

              {/* Status & Actions */}
              <div className="flex items-center justify-between">
                <Badge className={ALLOCATION_STATUS[allocationDetail.allocation.status]?.color || 'bg-gray-100'}>
                  {ALLOCATION_STATUS[allocationDetail.allocation.status]?.label || allocationDetail.allocation.status}
                </Badge>
                <div className="flex gap-2">
                  {canEdit && allocationDetail.allocation.status === 'CALCULATED' && (
                    <Button size="sm" onClick={() => handleApprove(allocationDetail.allocation.id)}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approva
                    </Button>
                  )}
                  {canEdit && (allocationDetail.allocation.status === 'APPROVED' || allocationDetail.allocation.status === 'CALCULATED') && (
                    <Button size="sm" variant="outline" onClick={() => handleDistribute(allocationDetail.allocation.id)}>
                      <Send className="h-4 w-4 mr-2" />
                      Distribuisci Documenti
                    </Button>
                  )}
                </div>
              </div>

              {/* Lines Table */}
              <div>
                <h4 className="font-medium mb-3">Quote per Unità ({allocationDetail.lines?.length || 0})</h4>
                <div className="max-h-80 overflow-y-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Unità</TableHead>
                        <TableHead className="text-right">Millesimi</TableHead>
                        <TableHead className="text-right">%</TableHead>
                        <TableHead className="text-right">Quota €</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allocationDetail.lines?.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell className="font-medium">{line.unit_number}</TableCell>
                          <TableCell className="text-right">{line.millesimi.toFixed(2)}</TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {(line.millesimi / 10).toFixed(2)}%
                          </TableCell>
                          <TableCell className="text-right font-medium text-green-600">
                            {formatCurrency(line.quota)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Distributed Info */}
              {allocationDetail.allocation.status === 'DISTRIBUTED' && (
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-purple-800">
                    <strong>Documenti distribuiti</strong> il {formatDate(allocationDetail.allocation.distributed_at)}.
                    I documenti sono visibili nella sezione Documenti.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
